import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { ViewContextService } from 'app/navigation/view-context.service';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { ConfirmDialogComponent } from 'app/shared/dialog/confirm-dialog.component';
import { Maybe, StorePermission, User } from 'app/shared/graph';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

import { ConsolidateUserDialogComponent, ConsolidateUserDialogData, ConsolidateUserDialogResult } from './consolidate-user-dialog.component';
import { UserService } from './user.service';

@Component({
    templateUrl: './user-page.component.html',
    styleUrls: ['./user-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatTabGroup, MatTab, MatButton, MatIcon, ErrorComponent, CopyToClipboardComponent, RouterLink, MatDivider, DatePipe, FromNowPipe]
})
export class UserPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly userService = inject(UserService);
    private readonly authService = inject(AuthorizationService);
    private readonly matDialog = inject(MatDialog);
    private readonly viewContextService = inject(ViewContextService);

    userId: string;
    user?: Maybe<User>;

    authContext: AuthorizationContext;

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.userId = params.userId;
            this.subscribeWithGenericLoadingErrorHandling(this.userService.getUser(this.userId), (user) => {
                if (user) {
                    this.authService.authorizationContext().subscribe(authContext => this.authContext = authContext);
                    this.user = user;
                    this.viewContextService.selectStore(user.storeId);
                }
            });
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    deleteUser(user: User) {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Confirmation',
                message: `Do you really want to delete this user?`
            }
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.subscribeWithGenericSavingErrorHandling(this.userService.deleteUser({ id: user.id }), () => {
                this.subscribeWithGenericLoadingErrorHandling(this.userService.getUser(this.userId), (user) => {
                    this.user = user;
                });
            });
        });
    }

    canManageUser(user: User) {
        return this.authContext.hasStorePermissions(user.store.id, user.store.subscriptionId, StorePermission.ManageStoreUsers);
    }

    openConsolidateDialog(user: User) {
        let dialogRef = this.matDialog.open<ConsolidateUserDialogComponent, ConsolidateUserDialogData, ConsolidateUserDialogResult>(
            ConsolidateUserDialogComponent, {
                data: {
                    anonymousStoreUSerId: user.storeUserId
                }
            });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            this.subscribeWithGenericSavingErrorHandling(this.userService.consolidateUser({
                storeId: user.storeId,
                anonymousStoreUserId: user.storeUserId,
                identifiedStoreUserId: result.targetStoreUserId
            }), consolidatedUser => {
                this.router.navigate(['..', consolidatedUser.identifiedHubUserId], { relativeTo: this.route }).then();
            });
        });
    }
}
